import type { GQL_ID, NestedFilter } from '@@partner:types:graphql'

import React, { useState, useCallback, useEffect } from 'react'
import { CheckPicker, Tag } from 'rsuite'
import { useLazyFetchAllBusinessProfiles } from '@@partner:graphql/query/businessProfiles/allBusinessProfiles.gql'
import { getSearchableBusinessProfile } from '@@partner:helpers/order'
import AppLoader from '@components/common/AppLoader'

export interface BusinessProfilesPickerProps {
  value: GQL_ID[]
  onChange: (value: GQL_ID[]) => void
  placeholder?: string
  size?: 'lg' | 'md' | 'sm' | 'xs'
}

const defaultFilter: NestedFilter = {
  fields: [ 'tin', 'name' ],
  op: 'unwrap'
}

export default function BusinessProfilesPicker ({
  value,
  onChange,
  placeholder = 'Компании',
  size = 'sm'
}: BusinessProfilesPickerProps) {
  const [ filter, setFilter ] = useState<NestedFilter>(defaultFilter)

  const [ fetchBusinessProfiles, { data, loading, called } ] = useLazyFetchAllBusinessProfiles({
    pager: { limit: 100 },
    filter,
    sort: { fieldName: 'updated_at', direction: 0 }
  })

  // Загружаем данные при маунте, если есть предзаполненное значение
  useEffect(() => {
    if (value?.length > 0 && !called) {
      fetchBusinessProfiles()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const businessProfiles = getSearchableBusinessProfile(data?.allBusinessProfiles?.edges) ?? []

  const handleSearch = useCallback((keyword: string) => {
    if (!keyword) {
      setFilter(defaultFilter)
      return
    }
    setFilter({
      or: [
        { fields: [ 'tin' ], op: 'starts_with', value: [ keyword ] },
        { fields: [ 'name' ], op: 'starts_with', value: [ keyword ] }
      ]
    })
  }, [])

  return (
    <CheckPicker
      value={value}
      onChange={onChange}
      data={businessProfiles}
      onOpen={fetchBusinessProfiles}
      searchable
      placeholder={placeholder}
      valueKey="value"
      labelKey="label"
      size={size}
      block
      onSearch={handleSearch}
      renderMenu={(menu) => {
        if (loading || !data) {
          return <AppLoader.Inline content="Загружаем" />
        }
        return menu
      }}
      renderMenuItem={(label, item: any) => (
        <Tag key={item?.value} color={item?.color}>
          {label}
        </Tag>
      )}
    />
  )
}
